/**
 * Target Module - Shared Types and Models
 * Comprehensive type definitions for Target management
 */

// Import existing Quarter and Cycle types
import { Quarter } from '../master-data/quarter.interface';
import { Cycle } from '../master-data/cycle.interface';

// Re-export for convenience
export type { Quarter, Cycle };

// ==================== ENUMS ====================

/**
 * Type Based On
 */
export enum TypeBasedOn {
  CATEGORY = 1,
  SKU = 2,
}

/**
 * Type of Target
 */
export enum TypeOfTarget {
  MONTH = 1,
  QUARTER = 2,
  CYCLE = 3,
}

/**
 * Achievement Logic Type
 */
export enum AchievementLogic {
  OVERRIDE = 1,
  CUMULATIVE = 2,
}

/**
 * Ledger Type
 */
export enum LedgerType {
  POINTS = 1,
  CASH = 2,
}

/**
 * Quantity Type
 */
export enum QuantityType {
  ROUND = 1,
  FLOOR = 2,
  CEIL = 3,
  AS_IS = 4,
}

/**
 * Month Enum (1-12)
 */
export enum Month {
  JANUARY = 1,
  FEBRUARY = 2,
  MARCH = 3,
  APRIL = 4,
  MAY = 5,
  JUNE = 6,
  JULY = 7,
  AUGUST = 8,
  SEPTEMBER = 9,
  OCTOBER = 10,
  NOVEMBER = 11,
  DECEMBER = 12,
}

/**
 * Target Import Status
 */
export enum TargetImportStatus {
  PENDING = 0,
  APPROVED = 1,
  REJECTED = 2,
}

// ==================== INTERFACES ====================

/**
 * Target Basic Info
 */
export interface TargetBasicInfo {
  target_id?: number;
  target_name: string;
  type_of_based: TypeBasedOn;
  type_of_target: TypeOfTarget;
  achievement_upload_date?: string;
  description: string;
  achievement_logic: AchievementLogic;
  qty_type: QuantityType;
  ledger_type: LedgerType;
  trigger_notification?: boolean;
  notify_flag?: number; // API returns 0 or 1
  notification_content?: string;

  // User type and group
  user_type_id?: number;
  user_group_id?: number;

  // Conditional fields based on type_of_target
  month?: Month;
  year?: number;
  cycle_id?: number;
  quater_id?: number;
  quarter_id?: number; // API may return as quarter_id

  // Response fields
  type_of_based_name?: string;
  type_of_target_name?: string;
  quarter_name?: string;
  target_cycle_name?: string;
  achievement_date?: string; // API returns this instead of achievement_upload_date
  achievement_logic_name?: string;
  qty_type_name?: string;
  ledger_type_name?: string;
  status?: number;
  created_date?: string;
  updated_date?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Point Structure Template (for linking)
 */
export interface TargetPointStructureTemplate {
  template_id: number;
  template_name: string;
  type_of_pointstructure_id: number;
  type_of_slab_id: number;
  point_type_id?: number;
  typeof_cummulative_id?: number;
  sub_type_of_cumulative_id?: number;
  min_threshold?: number;
  max_threshold?: number;
  template_details: TargetTemplateDetail[];
}

/**
 * Template Detail for Point Configuration
 */
export interface TargetTemplateDetail {
  brand_id?: number;
  brand_name?: string;
  brand_ids?: number[];
  brand_details?: string;
  min_threshold?: number;
  max_threshold?: number;
  points_start_slab?: number;
  points_end_slab?: number;
  points_multiplier?: number;
  points_value?: number;
  total_points?: number;
}

/**
 * Point Configuration Payload
 */
export interface TargetPointConfigPayload {
  config_template_id: number;
  data: TargetPointStructureTemplate[];
  target_id: number;
  program_id: number;
}

/**
 * Target Import Record (for assign-users step)
 */
export interface TargetImportRecord {
  import_id: number;
  target_id: number;
  outlet_code: string;
  mobile_number: string;

  // Month-based
  month?: string;
  year?: number;

  // Quarter-based
  quarter_name?: string;

  // Cycle-based
  cycle_name?: string;

  // Target values
  total_target?: number;
  brand_targets?: { [key: string]: number };

  // Status
  status: TargetImportStatus;
  rejection_reason?: string;

  created_at?: string;
  updated_at?: string;
}

/**
 * Excel Upload Response
 */
export interface TargetExcelUploadResponse {
  // API returns these field names
  approved_count?: number;
  rejected_count?: number;
  // Alias for convenience
  approved?: number;
  rejected?: number;
  rejected_reason?: TargetRejectedRecord[];
  message?: string;
  status?: string; // API also returns status message
}

/**
 * Rejected Record Detail
 */
export interface TargetRejectedRecord {
  outlet_code: string;
  mobile_number: string;
  reason: string;
  row_number?: number;
}

/**
 * Target with Full Details (response from API)
 */
export interface TargetFullDetails {
  target_data: TargetBasicInfo[];
  points_structure: TargetPointStructureTemplate[];
  target_imports?: TargetImportRecord[];
}

/**
 * Target List Item (for view-targets table)
 */
export interface TargetListItem {
  target_id: number;
  target_name: string;
  type_of_based_name: string;
  type_of_target_name: string;
  quarter_name?: string;
  target_cycle_name?: string;
  achievement_date: string;
  month?: number;
  year?: number;
  created_at: string;
}

/**
 * Create Target Request
 */
export interface CreateTargetRequest extends TargetBasicInfo {}

/**
 * Update Target Request
 */
export interface UpdateTargetRequest extends TargetBasicInfo {
  id: number;
}

/**
 * Excel Template Configuration
 */
export interface TargetExcelTemplateConfig {
  fileName: string;
  sheetName: string;
  headers: string[];
  body: any[];
  multipleSheets: boolean;
  sheets: ExcelSheetConfig[];
}

/**
 * Excel Sheet Configuration (for dropdowns)
 */
export interface ExcelSheetConfig {
  name: string;
  key: string;
  hasDropdown: boolean;
  column: string;
  body: any[];
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Get Type Based On Label
 */
export function getTypeBasedOnLabel(type: TypeBasedOn): string {
  switch (type) {
    case TypeBasedOn.CATEGORY:
      return 'Category';
    case TypeBasedOn.SKU:
      return 'SKU';
    default:
      return 'Unknown';
  }
}

/**
 * Get Type of Target Label
 */
export function getTypeOfTargetLabel(type: TypeOfTarget): string {
  switch (type) {
    case TypeOfTarget.MONTH:
      return 'Month';
    case TypeOfTarget.QUARTER:
      return 'Quarter';
    case TypeOfTarget.CYCLE:
      return 'Cycle';
    default:
      return 'Unknown';
  }
}

/**
 * Get Achievement Logic Label
 */
export function getAchievementLogicLabel(logic: AchievementLogic): string {
  switch (logic) {
    case AchievementLogic.OVERRIDE:
      return 'Override';
    case AchievementLogic.CUMULATIVE:
      return 'Cumulative';
    default:
      return 'Unknown';
  }
}

/**
 * Get Ledger Type Label
 */
export function getLedgerTypeLabel(type: LedgerType): string {
  switch (type) {
    case LedgerType.POINTS:
      return 'Points';
    case LedgerType.CASH:
      return 'Cash';
    default:
      return 'Unknown';
  }
}

/**
 * Get Quantity Type Label
 */
export function getQuantityTypeLabel(type: QuantityType): string {
  switch (type) {
    case QuantityType.ROUND:
      return 'Round';
    case QuantityType.FLOOR:
      return 'Floor';
    case QuantityType.CEIL:
      return 'Ceil';
    case QuantityType.AS_IS:
      return 'As Is';
    default:
      return 'Unknown';
  }
}

/**
 * Get Month Label
 */
export function getMonthLabel(month: Month): string {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return months[month - 1] || 'Unknown';
}

/**
 * Get Import Status Label
 */
export function getImportStatusLabel(status: TargetImportStatus): string {
  switch (status) {
    case TargetImportStatus.PENDING:
      return 'Pending';
    case TargetImportStatus.APPROVED:
      return 'Approved';
    case TargetImportStatus.REJECTED:
      return 'Rejected';
    default:
      return 'Unknown';
  }
}

/**
 * Get Import Status Badge Class
 */
export function getImportStatusBadgeClass(status: TargetImportStatus): string {
  switch (status) {
    case TargetImportStatus.PENDING:
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    case TargetImportStatus.APPROVED:
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case TargetImportStatus.REJECTED:
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

/**
 * Validate Target Form Based on Type
 */
export function validateTargetByType(
  type: TypeOfTarget,
  formData: Partial<TargetBasicInfo>
): string[] {
  const errors: string[] = [];

  if (type === TypeOfTarget.MONTH) {
    if (!formData.month) errors.push('Month is required');
    if (!formData.year) errors.push('Year is required');
  } else if (type === TypeOfTarget.QUARTER) {
    if (!formData.quater_id) errors.push('Quarter is required');
  } else if (type === TypeOfTarget.CYCLE) {
    if (!formData.cycle_id) errors.push('Cycle is required');
  }

  return errors;
}
